// NOTE: NSAI-6881 — what an API key may do on the external API (`/v1`). Mirrors
// the server's EApiKeyScope; the values are a public contract and never renamed.
export enum EApiKeyScope {
  CHAT = "chat",
  IMAGES = "images",
  MODELS = "models",
  FILES = "files",
  ASSISTANTS_READ = "assistants:read",
  ASSISTANTS_WRITE = "assistants:write",
  SKILLS_READ = "skills:read",
  PROMPTS_READ = "prompts:read",
  PROMPTS_WRITE = "prompts:write",
  AGENTS_READ = "agents:read",
  AGENTS_RUN = "agents:run",
  WORKFLOWS_READ = "workflows:read",
  WORKFLOWS_WRITE = "workflows:write",
  WORKFLOWS_RUN = "workflows:run",
}

// NOTE: NSAI-6881 — null is an unrestricted key (every scope, including ones
// added later); an array is the exact set the key may use, never empty.
export type TApiKeyScopes = EApiKeyScope[] | null;

// NOTE: NSAI-6881 — i18n key segment under `apiKeys.scopes.groups`.
export type TApiKeyScopeGroupId =
  | "chat"
  | "assistants"
  | "skills"
  | "prompts"
  | "agents"
  | "workflows";

export interface IApiKeyScopeGroup {
  id: TApiKeyScopeGroupId;
  scopes: readonly EApiKeyScope[];
}

export interface IToggleApiKeyScopeProps {
  scopes: readonly EApiKeyScope[];
  scope: EApiKeyScope;
}

export interface IUpdateApiKeyScopesProps {
  uuid: string;
  projectUuid: string;
  scopes: TApiKeyScopes;
}

export interface IUpdateApiKeyScopesResponse {
  uuid: string;
  scopes: TApiKeyScopes;
}

export interface IApiKey {
  uuid: string;
  name: string;
  keyPrefix: string;
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  deletedAt: string | null;
  createdBy: string | null;
  ownerEmail: string | null;
  projectUuid: string | null;
  projectTitle: string | null;
  // NOTE: NSAI-6881 — optional so a server without scopes reads as unrestricted.
  scopes?: TApiKeyScopes;
}

export interface ICreateApiKeyBody {
  projectUuid: string;
  name: string;
  expiresAt?: string | null;
  // NOTE: NSAI-6881 — omitted or null creates an unrestricted key.
  scopes?: TApiKeyScopes;
}

export interface ICreateApiKeyResponse {
  uuid: string;
  name: string;
  key: string;
  createdAt: string;
  expiresAt: string | null;
  scopes?: TApiKeyScopes;
}

export interface IGetApiKeysProps {
  projectUuid: string;
}

export interface IDeleteApiKeyProps {
  uuid: string;
  projectUuid: string;
}

export interface IApiKeyLog {
  uuid: string;
  endpoint: string;
  modelName: string | null;
  status: string;
  // NOTE: NSAI-5961 — null means the provider reported no usage for the call
  // (every OpenAI image generation does), which is unknown rather than zero. It
  // renders as the same em dash the Token & Cost Usage screen uses, never as 0.
  totalTokens: number | null;
  createdAt: string;
  apiKey?: {
    name: string;
    isDeleted: boolean;
  };
  ownerEmail: string | null;
  // NOTE: NSAI-4653 — carried alongside the title so a client can tell which
  // row is the personal project and label it from translations instead of the
  // signup-locale string the server stored.
  projectUuid: string | null;
  projectTitle: string | null;
}

export interface IGetApiKeyLogsParams {
  limit?: number;
  offset?: number;
}

export interface IGetProjectApiKeyLogsParams extends IGetApiKeyLogsParams {
  projectUuid: string;
}

export interface IGetOrganizationApiKeyLogsParams extends IGetApiKeyLogsParams {
  organizationUuid: string;
}

export interface IGetApiKeyLogsByKeyParams extends IGetApiKeyLogsParams {
  uuid: string;
  projectUuid: string;
}

export interface IGetApiKeyLogsResponse {
  logs: IApiKeyLog[];
  total: number;
}

export enum EApiKeyLogStatus {
  SUCCESS = "success",
  BLOCKED = "blocked",
  ERROR = "error",
}

export interface IBulkDeleteApiKeysByUsersBody {
  userUuids: string[];
}

export interface IBulkDeleteApiKeysByUsersResponse {
  deletedCount: number;
}

export interface IUserWithActiveKeys {
  uuid: string;
  email: string;
  activeKeyCount: number;
}

export interface IGetUsersWithActiveKeysProps {
  projectUuid: string;
}

export type TExpirationPreset =
  | "7d"
  | "30d"
  | "60d"
  | "90d"
  | "custom"
  | "none";

export interface IGetPresetExpirationDateProps {
  value: TExpirationPreset;
}

export type TApiKeyStatus = "active" | "expired" | "deleted";

export interface IGetApiKeyStatusProps {
  expiresAt: string | null;
  deletedAt: string | null;
}

export interface IResolveProjectLabelProps {
  projectUuid: string | null;
  projectTitle: string | null;
  personalProjectUuid: string | null;
  personalProjectLabel: string;
}
